import { Flex, Text } from '@chakra-ui/react';
import { faBell, faClock } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Accordion, Badge, ButtonText } from '@morressier/alembic';
import { useState } from 'react';
import { useReviewData } from 'src/contexts/ReviewDataProvider';
import { calculateDaysToDeadline } from 'src/utils/deadlines';
import { RequestReviewDeadlineExtensionModal } from './RequestReviewDeadlineExtensionModal';
import { IconBadge } from 'src/components/IconBadge';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import { ReviewDetailsTab } from 'src/pages/Review';
import { CoReviewHighlight } from '../CoReviewHighlight';
import { timeFormatDeadlineDate } from '../../../../../utils/date';
import { ReviewScoreHighlight } from '../ReviewScoreHighlight';

export const ReviewerPeerReviewAdditionalInfo = ({
  setActiveTab
}: {
  setActiveTab: (tab: ReviewDetailsTab) => void;
}) => {
  const { review, hasSubmittedReviewInCurrentRound, availableActions } =
    useReviewData();
  const [isExtensionRequestedModal, setIsExtensionRequestedModal] =
    useState<boolean>(false);

  const reviewDeadline = new Date(review.workflow.currentDeadline ?? '2024');
  const daysToDeadline = calculateDaysToDeadline(reviewDeadline);
  const extensionRequested =
    review.workflow.deadlineExtensions?.status === 'requested';

  return (
    <AdditionalInformationWrapper>
      {isExtensionRequestedModal && (
        <RequestReviewDeadlineExtensionModal
          submissionId={review.caseData.submission.id}
          submissionContextId={review.submissionContextId}
          onClose={() => setIsExtensionRequestedModal(false)}
        />
      )}
      <Accordion
        chakraAccordionProps={{
          width: '100%',
          defaultIndex: 0
        }}
        items={[
          {
            title: (
              <Flex alignItems={'flex-start'} gap={2}>
                <IconBadge icon={faBell} variant="neutral" />
                <Flex flexDirection={'column'} marginLeft={2}>
                  <Text textStyle="headingItem">Highlights</Text>
                  <Text textStyle="textContext">
                    Review key updates and assessment information for this
                    revision phase.
                  </Text>
                </Flex>
              </Flex>
            ),
            content: (
              <Flex flexDirection="column" width="100%" gap={4}>
                {availableActions.contains(
                  'RequestReviewDeadlineExtension'
                ) && (
                  <Flex alignItems="center" gap={4}>
                    <Badge
                      variant="attention"
                      size="context"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Flex paddingX={1}>
                        <FontAwesomeIcon
                          icon={faClock}
                          style={{ height: '24px', width: '16px' }}
                        />
                      </Flex>
                    </Badge>
                    <Flex flex={1} flexDirection="column">
                      <Text textStyle="headingItem">
                        Deadline on {timeFormatDeadlineDate(reviewDeadline)}
                      </Text>
                      <Text textStyle="textBody">
                        {review.workflow.deadlineExtensions?.status === 'denied'
                          ? 'The editors have rejected your last extension request.'
                          : `You have ${daysToDeadline} days to submit this review.`}
                      </Text>
                    </Flex>
                    {extensionRequested ? (
                      <Badge>
                        <Text textStyle="headingItem">Extension requested</Text>
                      </Badge>
                    ) : (
                      <ButtonText
                        onClick={() => setIsExtensionRequestedModal(true)}
                        isDisabled={hasSubmittedReviewInCurrentRound()}
                      >
                        Request Extension
                      </ButtonText>
                    )}
                  </Flex>
                )}
                <ReviewScoreHighlight setActiveTab={setActiveTab} />
                <CoReviewHighlight />
              </Flex>
            )
          }
        ]}
      />
    </AdditionalInformationWrapper>
  );
};
